import { TFunction } from 'i18next'
import { DataTableColumn } from '@/components/ui/data-table-customized'
import {
    ServiceConfigoption,
    Service,
    PeekSettings,
} from '@/modules/Service/types'
import { Badge } from '@/components/ui/badge'
import { UNLIMITED } from '@/modules/Contract/constants'

// ============================================================================
// TYPES
// ============================================================================

export interface SettingRow extends ServiceConfigoption {
    _rowId: string
    _rowType: 'item' | 'summary'
    _isEditable?: boolean
}

export interface SettingTotals {
    totalCommitment: number
    totalPrice: number
}

export interface CreateSettingColumnsOptions {
    t: TFunction
    pricingModel?: string
    peekSettings?: PeekSettings[]
    viewOnly?: boolean
}

// ============================================================================
// DATA TRANSFORMATION
// ============================================================================

interface TransformSettingDataParams {
    data: ServiceConfigoption[] | undefined
    service: Service
    t: TFunction
    filterStatementable?: boolean
}

/**
 * Transform config options data into rows for DataTable
 */
export function transformSettingData({
    data,
    t,
    filterStatementable = true,
}: TransformSettingDataParams): SettingRow[] {
    if (!data || data.length === 0) return []

    const rows: SettingRow[] = []

    // Filter out statementable items if requested
    const configItems = filterStatementable
        ? data.filter((item) => !item.statementable)
        : data

    // Add item rows
    configItems.forEach((item, index) => {
        rows.push({
            ...item,
            _rowId: `item-${item.service_configoption_id || index}`,
            _rowType: 'item',
            _isEditable: true,
        })
    })

    // Calculate totals
    if (rows.length > 0) {
        const totals = calculateSettingTotals(configItems)
        rows.push({
            _rowId: 'summary',
            _rowType: 'summary',
            name: t('total', { defaultValue: 'Tổng cộng' }),
            commitment_value: totals.totalCommitment,
            commitment_price_total: totals.totalPrice,
        } as SettingRow)
    }

    return rows
}

/**
 * Calculate totals from config items
 */
export function calculateSettingTotals(
    items: ServiceConfigoption[]
): SettingTotals {
    return items.reduce(
        (acc, item) => {
            const value = item.commitment_value || 0
            const price = item.commitment_price_total || 0

            // Skip unlimited values in totals
            if (value < UNLIMITED) {
                acc.totalCommitment += value
            }
            acc.totalPrice += price

            return acc
        },
        { totalCommitment: 0, totalPrice: 0 }
    )
}

// ============================================================================
// COLUMN DEFINITIONS
// ============================================================================

/**
 * Create columns for Setting table based on options
 */
export function createSettingColumns({
    t,
    pricingModel,
    peekSettings,
}: CreateSettingColumnsOptions): DataTableColumn<SettingRow>[] {
    const isPackage = pricingModel === 'package'
    const showField = (field: PeekSettings) =>
        !peekSettings || peekSettings.includes(field)

    const columns: DataTableColumn<SettingRow>[] = []

    // Name column - always shown
    if (showField('name')) {
        columns.push({
            key: 'name',
            title: t('services:config_name', { defaultValue: 'Cấu hình' }),
            width: 200,
            pinned: 'left',
            render: (value, row) => {
                if (row._rowType === 'summary') {
                    return (
                        <span className="font-semibold text-slate-900">
                            {value}
                        </span>
                    )
                }
                return (
                    <div className="flex items-center gap-2">
                        <span>{t(value) || value}</span>
                        {row.statementable && (
                            <Badge
                                variant="outline"
                                className="text-[10px] px-1.5 py-0 bg-amber-50 text-amber-700 border-amber-200"
                            >
                                STATEMENT
                            </Badge>
                        )}
                    </div>
                )
            },
        })
    }

    // Unit column
    columns.push({
        key: 'unit',
        title: t('services:unit', { defaultValue: 'Đơn vị' }),
        width: 100,
        render: (value, row) => {
            if (row._rowType === 'summary') return null
            return value || '-'
        },
    })

    // Commitment value column
    if (showField('commitment_value')) {
        columns.push({
            key: 'commitment_value',
            title: t('services:quantity', { defaultValue: 'Số lượng' }),
            width: 120,
            cellClassName: 'text-right',
            render: (value, row) => {
                if (row._rowType === 'summary') {
                    return (
                        <span className="font-semibold">
                            {new Intl.NumberFormat('en-US').format(value || 0)}
                        </span>
                    )
                }
                if (value >= UNLIMITED) {
                    return (
                        <Badge
                            variant="secondary"
                            className="bg-blue-50 text-blue-700"
                        >
                            Unlimited
                        </Badge>
                    )
                }
                return new Intl.NumberFormat('en-US').format(value || 0)
            },
        })
    }

    // Commitment available column
    if (showField('commitment_available')) {
        columns.push({
            key: 'commitment_available',
            title: t('services:available', { defaultValue: 'Khả dụng' }),
            width: 120,
            cellClassName: 'text-right',
            render: (value, row) => {
                if (row._rowType === 'summary') return null
                if (value === null || value === undefined) return '-'
                return new Intl.NumberFormat('en-US').format(value)
            },
        })
    }

    // Commitment time column
    if (showField('commitment_time')) {
        columns.push({
            key: 'commitment_time',
            title: t('services:commitment_time', {
                defaultValue: 'Thời hạn (tháng)',
            }),
            width: 130,
            cellClassName: 'text-right',
            render: (value, row) => {
                if (row._rowType === 'summary') return null
                return value || '-'
            },
        })
    }

    // Price per unit column (only for bundle pricing)
    if (showField('commitment_price_per_unit') && !isPackage) {
        columns.push({
            key: 'commitment_price_per_unit',
            title: t('services:unit_price', { defaultValue: 'Đơn giá' }),
            width: 130,
            cellClassName: 'text-right',
            render: (value, row) => {
                if (row._rowType === 'summary') return null
                return value
                    ? new Intl.NumberFormat('en-US').format(value)
                    : '-'
            },
        })
    }

    // Price total column
    if (showField('commitment_price_total')) {
        columns.push({
            key: 'commitment_price_total',
            title: t('services:total_price', { defaultValue: 'Thành tiền' }),
            width: 130,
            cellClassName: 'text-right',
            render: (value, row) => {
                if (row._rowType === 'summary') {
                    return (
                        <span className="font-semibold text-blue-600">
                            {new Intl.NumberFormat('en-US').format(value || 0)}
                        </span>
                    )
                }
                return value
                    ? new Intl.NumberFormat('en-US').format(value)
                    : '-'
            },
        })
    }

    // Prepaid column
    if (showField('commitment_prepaid')) {
        columns.push({
            key: 'commitment_prepaid',
            title: t('services:prepaid', { defaultValue: 'Trả trước' }),
            width: 100,
            render: (value, row) => {
                if (row._rowType === 'summary') return null
                return value ? (
                    <Badge
                        variant="secondary"
                        className="bg-green-50 text-green-700"
                    >
                        Yes
                    </Badge>
                ) : (
                    <span className="text-slate-400">No</span>
                )
            },
        })
    }

    // Statementable column
    if (showField('statementable')) {
        columns.push({
            key: 'statementable',
            title: t('services:statementable', {
                defaultValue: 'Đối soát',
            }),
            width: 100,
            render: (value, row) => {
                if (row._rowType === 'summary') return null
                return value ? (
                    <Badge
                        variant="secondary"
                        className="bg-amber-50 text-amber-700"
                    >
                        Yes
                    </Badge>
                ) : (
                    <span className="text-slate-400">No</span>
                )
            },
        })
    }

    return columns
}

// ============================================================================
// ROW STYLING
// ============================================================================

/**
 * Get row class name based on row type
 */
export function getSettingRowClassName(row: SettingRow): string {
    if (row._rowType === 'summary') {
        return 'bg-slate-50 border-t-2 border-slate-200 font-medium'
    }
    return 'hover:bg-slate-50/50'
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Check if service has commitment data
 */
export function hasCommitmentData(
    data: ServiceConfigoption[] | undefined
): boolean {
    if (!data || data.length === 0) return false
    return data.some(
        (item) => item.commitment_value && item.commitment_value > 0
    )
}
